import { useCallback, useRef, useState } from 'react';
import { Alert, Input, Modal, Typography } from 'antd';
import { useAuthStore } from '@/store/authStore';

export const TMMDE_NOT_USABLE_ERROR_CODE = 'TMMDE_EQUIPMENT_NOT_USABLE';
export const TMMDE_USE_OVERRIDE_PERMISSION = 'calibration.use_override';

type ApiError = { response?: { status?: number; data?: { code?: string; message?: string } } };

export function isTmmdeNotUsableError(error: unknown): boolean {
  const err = error as ApiError;
  return err?.response?.status === 409 && err.response?.data?.code === TMMDE_NOT_USABLE_ERROR_CODE;
}

export function useTmmdeUseOverrideAllowed(): boolean {
  const user = useAuthStore((s) => s.user);
  return Boolean(user?.is_super_admin || user?.permissions?.includes(TMMDE_USE_OVERRIDE_PERMISSION));
}

type Pending = {
  message: string;
  resolve: (reason: string | null) => void;
};

/**
 * Wraps a save that may be blocked because measuring equipment is not valid for use.
 * Holders of calibration.use_override are asked for a reason and the save is retried with it;
 * everyone else gets the original error.
 */
export function useTmmdeUseOverride() {
  const canOverride = useTmmdeUseOverrideAllowed();
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const pendingRef = useRef<Pending | null>(null);

  const askReason = useCallback(
    (msg: string) =>
      new Promise<string | null>((resolve) => {
        const next = { message: msg, resolve };
        pendingRef.current = next;
        setReason('');
        setPending(next);
      }),
    []
  );

  const close = (value: string | null) => {
    pendingRef.current?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  };

  const runWithOverride = useCallback(
    async <T,>(save: (overrideReason?: string) => Promise<T>): Promise<T> => {
      try {
        return await save();
      } catch (error) {
        if (!canOverride || !isTmmdeNotUsableError(error)) throw error;
        const msg = (error as ApiError).response?.data?.message || 'Measuring equipment is not valid for use.';
        const given = await askReason(msg);
        if (!given) throw error;
        return save(given);
      }
    },
    [askReason, canOverride]
  );

  const overrideModal = (
    <Modal
      open={Boolean(pending)}
      title="Use equipment under override"
      okText="Confirm use"
      okButtonProps={{ danger: true, disabled: !reason.trim() }}
      onOk={() => close(reason.trim())}
      onCancel={() => close(null)}
      destroyOnClose
    >
      <Alert type="warning" showIcon message={pending?.message} style={{ marginBottom: 12 }} />
      <Typography.Paragraph type="secondary">
        The override, your name and the reason are logged against the equipment and Quality is notified.
      </Typography.Paragraph>
      <Input.TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for using this equipment"
        maxLength={500}
        showCount
      />
    </Modal>
  );

  return { runWithOverride, overrideModal, canOverride };
}
